
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const SetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Check if this is a valid magic link session
  useEffect(() => {
    const validateSession = async () => {
      try {
        setValidating(true);
        
        // Get the auth info from the URL
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        
        if (!accessToken || !refreshToken || type !== 'recovery') {
          throw new Error('Ungültiger oder abgelaufener Link. Bitte fordern Sie einen neuen Link an.');
        }

        // Try to exchange the tokens for a session
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });
        
        if (error || !data.session) {
          throw new Error('Sitzung konnte nicht wiederhergestellt werden. Bitte fordern Sie einen neuen Link an.');
        }
        
        // Session is valid
        setValidating(false);
      } catch (error: any) {
        console.error('Session validation error:', error);
        setErrorMessage(error.message || 'Ein unbekannter Fehler ist aufgetreten.');
        setValidating(false);
      }
    };
    
    validateSession();
  }, []);

  const handleSetPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (password !== confirmPassword) {
      setErrorMessage('Passwörter stimmen nicht überein');
      return;
    }
    
    if (password.length < 6) {
      setErrorMessage('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    
    setLoading(true);
    setErrorMessage(null);
    
    try {
      // Update the user's password
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) throw error;
      
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('Benutzer nicht gefunden');
      
      // Check if the user has admin role
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .single();
        
      const isAdmin = roleData?.role === 'admin';
      
      // Mark user as no longer needing onboarding
      await supabase
        .from('profiles')
        .update({ needs_onboarding: false })
        .eq('id', user.id);
      
      // Log the user in
      login({
        id: user.id,
        email: user.email || '',
        name: user.user_metadata.name || user.email?.split('@')[0] || ''
      }, isAdmin);
      
      toast.success('Passwort erfolgreich gesetzt! Sie werden weitergeleitet...');
      
      // Redirect to the dashboard
      setTimeout(() => {
        navigate(isAdmin ? '/admin/dashboard' : '/dashboard');
      }, 1500);
      
    } catch (error: any) {
      console.error('Error setting password:', error);
      setErrorMessage(error.message || 'Ein Fehler ist aufgetreten beim Setzen des Passworts.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0f121a]">
        <Card className="w-full max-w-md bg-[#1c242f] border-gray-700 text-white shadow-lg">
          <CardHeader className="space-y-2 text-center">
            <CardTitle className="text-2xl text-white">Passwort setzen</CardTitle>
            <CardDescription className="text-gray-400">
              Link wird überprüft, bitte warten...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-[#41c7f0]" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (errorMessage && !password) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0f121a]">
        <Card className="w-full max-w-md bg-[#1c242f] border-gray-700 text-white shadow-lg">
          <CardHeader className="space-y-2 text-center">
            <CardTitle className="text-2xl text-white">Ungültiger Link</CardTitle>
            <CardDescription className="text-red-400">
              {errorMessage}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button 
              className="w-full bg-[#41c7f0] hover:bg-[#3ab1d9] text-white font-medium"
              onClick={() => navigate('/login')}
            >
              Zurück zum Login
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#0f121a]">
      <Card className="w-full max-w-md bg-[#1c242f] border-gray-700 text-white shadow-lg">
        <CardHeader className="space-y-2">
          <CardTitle className="text-2xl text-white">Passwort setzen</CardTitle>
          <CardDescription className="text-gray-400">
            Legen Sie ein sicheres Passwort für Ihren Account fest.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSetPassword} className="space-y-4">
            {errorMessage && (
              <div className="p-3 bg-red-900/30 border border-red-800 text-red-300 rounded-md text-sm">
                {errorMessage}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="password">Neues Passwort</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#2A2A2A] border-gray-600"
                required
                autoComplete="new-password"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-[#2A2A2A] border-gray-600"
                required
                autoComplete="new-password"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-[#41c7f0] hover:bg-[#3ab1d9] text-white font-medium"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird gespeichert...
                </>
              ) : "Passwort setzen"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetPassword;
